import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { login } from '@/features/auth/api';
import { ApiError } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

const loginSearchSchema = z.object({
  /** Path original que o user tentou acessar — redireciona pra cá após login */
  next: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  validateSearch: loginSearchSchema,
  beforeLoad: () => {
    // Se já autenticado, não exibe /login — manda direto pra raiz.
    if (useAuthStore.getState().status === 'authenticated') {
      throw redirect({ to: '/' });
    }
  },
  component: LoginPage,
});

function LoginPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const loginAction = useAuthStore((s) => s.login);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await login(username, password);
      loginAction(
        { accessToken: res.access_token, refreshToken: res.refresh_token, username },
        remember,
      );
      const next = search.next?.startsWith('/') ? search.next : '/';
      await navigate({ to: next });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setError('Usuário ou senha inválidos.');
        } else if (err.status === 0) {
          setError('Sem conexão com o servidor — verifica sua rede.');
        } else {
          setError(`${err.message} (HTTP ${err.status})`);
        }
      } else {
        setError('Erro inesperado. Tenta de novo.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center bg-bg p-6 text-fg">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-border bg-bg-elevated p-6 shadow-md"
      >
        <header className="space-y-1">
          <h1 className="text-xl font-semibold tracking-tight">techdash</h1>
          <p className="text-sm text-fg-muted">Entre com suas credenciais para continuar.</p>
        </header>

        {/* TD26-28b: label htmlFor explícito (Biome a11y rule não tracea
            label wrapping custom React component — htmlFor é mais robusto +
            melhor pra screen readers anyway). */}
        <div className="space-y-1">
          <label htmlFor="login-username" className="block text-sm font-medium">
            Usuário
          </label>
          <Input
            id="login-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            required
            autoFocus
            disabled={submitting}
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="login-password" className="block text-sm font-medium">
            Senha
          </label>
          <div className="relative">
            <Input
              id="login-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting}
              className="pr-10"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-fg-muted"
            >
              {showPassword ? '🙈' : '👁️'}
            </Button>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            disabled={submitting}
            className="h-4 w-4 rounded border-border accent-[color:var(--accent)]"
          />
          <span>Lembrar de mim neste dispositivo</span>
        </label>

        {error && (
          <div
            role="alert"
            className="rounded-md border border-status-critical bg-status-critical/10 px-3 py-2 text-sm text-status-critical"
          >
            {error}
          </div>
        )}

        {/* TD26-28b: Button shadcn variant=default size=lg pra CTA primário. */}
        <Button
          type="submit"
          size="lg"
          disabled={submitting || username.length === 0 || password.length === 0}
          className="w-full"
        >
          {submitting ? 'Entrando…' : 'Entrar'}
        </Button>
      </form>
    </div>
  );
}
